/**
 * useVisitorSettings
 *
 * Call this once near the top of your app (e.g. inside App.tsx or a root layout).
 *
 * What it does:
 *  - Fetches { accentColor, theme } from the backend on mount
 *  - Applies accentColor to CSS variables globally (visitors cannot change this)
 *  - Applies the saved default theme ONLY if the visitor has no personal
 *    preference stored in localStorage (key: "nidave-visitor-theme")
 *  - Provides a `toggleTheme` function that visitors can use — it only
 *    touches localStorage and the DOM, never the backend
 */

import { useEffect, useState } from 'react';
import { getSetting } from '../services/api'; // adjust path as needed
import { applyAccentGlobal, applyThemeGlobal } from '../pages/AdminDashboard'; // re-export from AdminDashboard

const VISITOR_THEME_KEY = 'nidave-visitor-theme';

export const useVisitorSettings = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // Restore visitor's personal preference immediately (avoids flash)
    const stored = localStorage.getItem(VISITOR_THEME_KEY) as 'light' | 'dark' | null;
    return stored ?? 'light';
  });

  useEffect(() => {
    (async () => {
      try {
        const r    = await getSetting();
        const data = r?.data;

        // Always apply the admin-controlled accent color
        if (data?.accentColor) {
          applyAccentGlobal(data.accentColor);
        }

        // Apply default theme only when visitor has no personal preference
        const visitorPreference = localStorage.getItem(VISITOR_THEME_KEY);
        if (!visitorPreference && data?.theme) {
          setTheme(data.theme as 'light' | 'dark');
          applyThemeGlobal(data.theme as 'light' | 'dark');
        } else if (visitorPreference) {
          // Re-apply the visitor's stored preference to the DOM
          applyThemeGlobal(visitorPreference as 'light' | 'dark');
        }
      } catch {
        // Network error — just leave whatever the DOM currently shows
      }
    })();
  }, []);

  // Visitors toggle theme locally — does NOT hit the backend
  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    applyThemeGlobal(next);
    localStorage.setItem(VISITOR_THEME_KEY, next);
  };

  return { theme, toggleTheme };
};